(() => {
  const root = document.getElementById('sowelu-crystal-guide');
  if (!root || root.dataset.coreV3Initialized === 'true') return;
  root.dataset.coreV3Initialized = 'true';

  if (window.location.search.includes('preview_theme_id')) {
    root.classList.add('sowelu-guide--preview');
  }

  const endpoint = String(root.dataset.endpoint || '').trim();
  const panel = root.querySelector('[data-guide-panel]');
  const launcher = root.querySelector('[data-guide-launcher]');
  const greeting = root.querySelector('[data-guide-greeting]');
  const messages = root.querySelector('[data-guide-messages]');
  const form = root.querySelector('[data-guide-form]');
  const input = root.querySelector('[data-guide-input]');
  const sendButton = root.querySelector('[data-guide-send]');
  const suggestions = root.querySelector('[data-guide-suggestions]');
  const micButton = root.querySelector('[data-guide-mic]');

  if (!panel || !launcher || !messages || !form || !input || !sendButton || !suggestions) return;

  let previousResponseId = null;
  let cartId = null;
  let busy = false;

  const validEndpoint = /^https:\/\//i.test(endpoint) && !endpoint.includes('__SOWELU_CRYSTAL_GUIDE_ENDPOINT__');

  function setGreetingVisible(visible) {
    if (!greeting) return;
    greeting.classList.toggle('is-visible', Boolean(visible));
  }

  function openGuide() {
    setGreetingVisible(false);
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    window.setTimeout(() => input.focus(), 50);
  }

  function closeGuide() {
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    launcher.focus();
  }

  function scrollToLatest() {
    messages.scrollTop = messages.scrollHeight;
  }

  function renderSafeTextWithLinks(container, text) {
    const tokenPattern = /(https:\/\/[^\s]+|\*\*[^*\n]+\*\*)/g;
    const parts = String(text || '').split(tokenPattern);

    parts.forEach((part) => {
      if (!part) return;
      if (/^https:\/\//i.test(part)) {
        const cleaned = part.replace(/[),.;!?]+$/, '');
        const suffix = part.slice(cleaned.length);
        const link = document.createElement('a');
        link.href = cleaned;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = cleaned;
        container.appendChild(link);
        if (suffix) container.appendChild(document.createTextNode(suffix));
        return;
      }
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        const strong = document.createElement('strong');
        strong.textContent = part.slice(2, -2);
        container.appendChild(strong);
        return;
      }
      container.appendChild(document.createTextNode(part));
    });
  }

  function appendMessage(text, role = 'assistant', extraClass = '') {
    const row = document.createElement('div');
    row.className = `sowelu-guide__message sowelu-guide__message--${role}${extraClass ? ` ${extraClass}` : ''}`;

    if (role === 'assistant') {
      const avatar = document.createElement('div');
      avatar.className = 'sowelu-guide__avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = '✦';
      row.appendChild(avatar);
    }

    const bubble = document.createElement('div');
    bubble.className = 'sowelu-guide__bubble';
    renderSafeTextWithLinks(bubble, text);
    row.appendChild(bubble);
    messages.appendChild(row);
    scrollToLatest();
    return row;
  }

  function setBusy(value) {
    busy = Boolean(value);
    input.disabled = busy;
    sendButton.disabled = busy;
    if (micButton) micButton.disabled = busy;
    suggestions.querySelectorAll('button').forEach((button) => { button.disabled = busy; });
  }

  function resizeInput() {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 112)}px`;
  }

  function resetConversation() {
    previousResponseId = null;
    cartId = null;
    messages.innerHTML = '';
    appendMessage('Welcome. Tell me what you’re looking for — an intention, a particular stone, a form you can wear or carry, or simply something you’d like to learn about.');
    input.value = '';
    input.style.height = '';
    input.focus();
  }

  async function sendMessage(rawMessage) {
    const message = String(rawMessage || '').trim();
    if (!message || busy) return;

    appendMessage(message, 'user');
    input.value = '';
    input.style.height = '';
    setBusy(true);
    const thinking = appendMessage('Thinking…', 'assistant', 'sowelu-guide__message--thinking');

    if (!validEndpoint) {
      thinking.remove();
      appendMessage('The Crystal Guide is being connected right now. Please check back shortly.');
      setBusy(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 65000);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          previous_response_id: previousResponseId,
          cart_id: cartId
        }),
        signal: controller.signal
      });

      let payload = null;
      try { payload = await response.json(); } catch (_) { payload = null; }
      thinking.remove();

      if (!response.ok || !payload || typeof payload.text !== 'string') {
        appendMessage('I’m having trouble connecting at the moment. Please try again in a little while.');
        return;
      }

      previousResponseId = payload.previous_response_id || null;
      cartId = payload.cart_id || cartId;
      appendMessage(payload.text);
    } catch (error) {
      thinking.remove();
      const timedOut = error && error.name === 'AbortError';
      appendMessage(timedOut
        ? 'That took longer than expected. Please try your question again.'
        : 'I’m having trouble connecting at the moment. Please try again in a little while.');
    } finally {
      window.clearTimeout(timeout);
      setBusy(false);
      input.focus();
    }
  }

  launcher.addEventListener('click', () => {
    if (panel.hidden) openGuide();
    else closeGuide();
  });

  root.querySelectorAll('[data-guide-open]').forEach((button) => button.addEventListener('click', openGuide));
  root.querySelector('[data-guide-close]')?.addEventListener('click', closeGuide);
  root.querySelector('[data-guide-greeting-close]')?.addEventListener('click', () => setGreetingVisible(false));
  root.querySelector('[data-guide-reset]')?.addEventListener('click', resetConversation);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    sendMessage(input.value);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      form.requestSubmit();
    }
  });

  input.addEventListener('input', resizeInput);

  suggestions.addEventListener('click', (event) => {
    const button = event.target.closest('[data-guide-prompt]');
    if (!button) return;
    openGuide();
    sendMessage(button.dataset.guidePrompt || '');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) closeGuide();
  });

  window.setTimeout(() => {
    if (panel.hidden) setGreetingVisible(true);
  }, 1100);
})();
